// Phase I (I-C, I3): branding v2 colour maths, shared by the web theme and the email layout.
// A tenant picks any colours; what we render is adjusted so text stays readable (WCAG 2.x
// contrast: 4.5:1 for text, 3:1 for large text and UI parts).

const HEX = /^#[0-9a-fA-F]{6}$/;
export const isHexColour = (v: unknown): v is string => typeof v === 'string' && HEX.test(v);

function rgb(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

const toHex = (c: [number, number, number]) => `#${c.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;

/** WCAG relative luminance of #rrggbb. */
export function luminance(hex: string): number {
  const [r, g, b] = rgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Mixes `hex` toward `to` by `amount` (0 to 1). */
export function mix(hex: string, to: string, amount: number): string {
  const a = rgb(hex);
  const b = rgb(to);
  return toHex([0, 1, 2].map((i) => a[i]! + (b[i]! - a[i]!) * amount) as [number, number, number]);
}

export const DARK_TEXT = '#111827';
export const LIGHT_TEXT = '#ffffff';

/** Dark or white text, whichever reads better on `bg`. */
export const readableOn = (bg: string) => (contrastRatio(bg, DARK_TEXT) >= contrastRatio(bg, LIGHT_TEXT) ? DARK_TEXT : LIGHT_TEXT);

/**
 * `colour`, darkened or lightened (toward whichever of black/white moves away from `against`)
 * just enough to reach `min` contrast with `against`.
 */
export function ensureContrast(colour: string, against: string, min: number): string {
  if (contrastRatio(colour, against) >= min) return colour;
  const toward = luminance(against) > 0.5 ? '#000000' : '#ffffff';
  for (let step = 1; step <= 20; step++) {
    const candidate = mix(colour, toward, step / 20);
    if (contrastRatio(candidate, against) >= min) return candidate;
  }
  return toward;
}

export interface BrandColours {
  primaryColor: string;
  accentColor: string;
}

export const DEFAULT_BRAND: BrandColours = { primaryColor: '#0f1729', accentColor: '#2563eb' };

// The surfaces the app draws on (apps/web/src/styles/index.css).
const SURFACE = { light: '#ffffff', dark: '#121826' } as const;

/** CSS custom properties for the web theme, one set per colour scheme. */
export function brandTokens(b: Partial<BrandColours>, scheme: 'light' | 'dark'): Record<string, string> {
  const primary = isHexColour(b.primaryColor) ? b.primaryColor : DEFAULT_BRAND.primaryColor;
  const picked = isHexColour(b.accentColor) ? b.accentColor : DEFAULT_BRAND.accentColor;
  const surface = SURFACE[scheme];
  // Links and accent text sit on the surface: 4.5:1.
  const accent = ensureContrast(picked, surface, 4.5);
  const accentFg = readableOn(accent);
  const sidebar = scheme === 'dark' ? mix(primary, '#000000', 0.35) : primary;
  const sidebarFg = readableOn(sidebar);
  const sidebarLight = sidebarFg === LIGHT_TEXT;
  return {
    '--accent': accent,
    '--accent-hover': mix(accent, scheme === 'dark' ? '#ffffff' : '#000000', 0.12),
    '--accent-soft': mix(accent, surface, scheme === 'dark' ? 0.8 : 0.9),
    '--accent-fg': accentFg,
    '--sidebar': sidebar,
    '--sidebar-fg': sidebarFg,
    '--sidebar-muted': ensureContrast(mix(sidebarFg, sidebar, 0.4), sidebar, 4.5),
    '--sidebar-active': mix(sidebar, sidebarLight ? '#ffffff' : '#000000', 0.1),
    '--sidebar-hover': mix(sidebar, sidebarLight ? '#ffffff' : '#000000', 0.06),
  };
}

/** Plain-English warnings for Settings ▸ Branding when a colour had to be adjusted. */
export function brandingContrastWarnings(b: Partial<BrandColours>): string[] {
  const warnings: string[] = [];
  const accent = isHexColour(b.accentColor) ? b.accentColor : DEFAULT_BRAND.accentColor;
  const primary = isHexColour(b.primaryColor) ? b.primaryColor : DEFAULT_BRAND.primaryColor;
  if (contrastRatio(accent, SURFACE.light) < 4.5) warnings.push('The accent colour is too light to read on white. Links and buttons use a darker shade of it.');
  if (contrastRatio(accent, SURFACE.dark) < 4.5) warnings.push('The accent colour is too dark to read in dark mode. Dark mode uses a lighter shade of it.');
  if (contrastRatio(primary, readableOn(primary)) < 4.5) warnings.push('Text on the primary colour is hard to read. Pick a darker or lighter primary colour.');
  return warnings;
}

/** Colours for the email header band (light only: email clients rarely honour dark mode). */
export function emailBrandColours(b: Partial<BrandColours>) {
  const primary = isHexColour(b.primaryColor) ? b.primaryColor : DEFAULT_BRAND.primaryColor;
  const accent = ensureContrast(isHexColour(b.accentColor) ? b.accentColor : DEFAULT_BRAND.accentColor, '#ffffff', 4.5);
  return { headerBg: primary, headerFg: readableOn(primary), accent, accentFg: readableOn(accent) };
}
